'use client';

import {
  cn,
  getValueFromInputEvent,
  type InputProps,
  TextareaProps as CoreTextareaProps,
  VariantProps,
} from '@scatterlab/zeta-core';
import { memo, Ref, useCallback, useState } from 'react';
import TextareaAutosize, {
  TextareaAutosizeProps,
  TextareaHeightChangeMeta,
} from 'react-textarea-autosize';

import { textField } from './styles';

export type TextareaProps = CoreTextareaProps &
  VariantProps<typeof textField> & {
    maxRows?: number;
    minRows?: number;
    onHeightChange?: (height: number, meta: TextareaHeightChangeMeta) => void;
    cacheMeasurements?: boolean;
    disabledDefaultStyles?: boolean;
    textAlignVertical?: 'top' | 'center' | 'bottom';
  };

const Textarea = ({
  className,
  maxRows = 10,
  minRows = 3,
  autoCorrect,
  onChange,
  onChangeText,
  disabledDefaultStyles,
  onFocus,
  onBlur,
  onKeyPress,
  onSubmitEditing,
  onSelectionChange,
  onContentSizeChange,
  textAlignVertical,
  ref,
  style,
  ...props
}: TextareaProps) => {
  const [focus, setFocus] = useState(false);
  const handleChange = useCallback<Required<InputProps>['onChange']>(
    (event) => {
      onChange?.(event);

      const text = getValueFromInputEvent(event);
      onChangeText?.(text);
    },
    [onChange, onChangeText],
  );

  const handleFocus = useCallback<Required<TextareaProps>['onFocus']>(
    (event) => {
      setFocus(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = useCallback<Required<TextareaProps>['onBlur']>(
    (event) => {
      setFocus(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleKeyPress = useCallback<Required<TextareaProps>['onKeyPress']>(
    (event) => {
      if (
        event.nativeEvent.key === 'Enter' &&
        (!('shiftKey' in event.nativeEvent) || !event.nativeEvent.shiftKey) &&
        onSubmitEditing
      ) {
        onSubmitEditing(getValueFromInputEvent(event));
        event.preventDefault();
        return;
      }
      onKeyPress?.(event);
    },
    [onKeyPress, onSubmitEditing],
  );

  return (
    <TextareaAutosize
      {...props}
      className={cn(
        disabledDefaultStyles ? false : textField({ textarea: true, focus }),
        className,
      )}
      maxRows={maxRows}
      minRows={minRows}
      autoComplete="off"
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      autoCorrect={autoCorrect ? 'true' : 'off'}
      ref={ref as Ref<HTMLTextAreaElement>}
      style={style as TextareaAutosizeProps['style']}
    />
  );
};

export default memo(Textarea);
